/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Component, Vue, Prop } from 'vue-property-decorator';
import request from '@hb/sharp-request';
import { LivenessView, isNeedAlwaysAgreeProtocol } from '@hb/liveness';
import { Toast } from '@hb/coffee';
import '@hb/liveness/dist/style.css';

import platform from '@/shared/utils/platform';
import { getH5Url, getAlipayMiniUrl, getWechatMiniUrl } from '@/shared/tools/startLiveness/redirectUrl';
import { saveLivenessActionUBT } from '@/shared/tools/startLiveness/utils';
import {
  ubtPage,
  ubtButtonNew,
  ubtModuleExposeNew,
} from '@/shared/utils/ubt';
import { jumpToMarketPage } from '@/shared/utils/jumpToRedirectPage';
import { landData } from '@/shared/utils/landData';

import { env as standardEnv, newGatewayUrl, customerServicePhone } from '@/constant';
import { LivenessProjectEnum, LivenessSceneEnum } from '@/enums/liveness-param';
import type { LivenessSDKResultType, LivenessSDKErrorType } from '@hb/liveness';


@Component({ name: 'LivenessPage' })
export default class LivenessPage extends Vue {
  @Prop(String)
  private scene!: LivenessSceneEnum;

  @Prop(String)
  private redirectUrl?: string;

  @Prop(Boolean)
  private isShowCancelBtn?: boolean;

  @Prop(String)
  private cancelBtnText?: string;

  @Prop(String)
  private commodityId = '';

  @Prop({
    type: Object,
    default: () => ({}),
  })
  private readonly requestApiParams!: Record<string, unknown>;

  @Prop({
    type: Object,
    default: () => undefined,
  })
  private readonly launchTransaction?: Record<string, unknown>;

  @Prop(Boolean)
  private readonly skipAgreementCheck?: boolean;

  /** 活体识别api请求设置 */
  private get requestOptions(): Record<string, unknown> {
    return {
      request,
      baseURL: newGatewayUrl,
      params: this.requestApiParams,
    };
  }

  private get livenessRedirectUrl(): string | undefined {
    if (platform.isHelloApp) {
      return undefined;
    }

    const params = {
      project: LivenessProjectEnum.ZYQ,
      scene: this.scene,
      redirectUrl: this.redirectUrl,
    };

    if (platform.isAlipayMini) {
      return getAlipayMiniUrl(params).redirectUrl;
    }

    if (platform.isWechatMini) {
      return getWechatMiniUrl(params).redirectUrl;
    }

    return getH5Url(params).redirectUrl;
  }

  /** 当前页面ubt通用参数 */
  private get ubtCommonParams() {
    return {
      scene: this.scene,
    };
  }

  private async getIsNeedAlwaysAgreeProtocol(): Promise<number> {
    try {
      const result = await isNeedAlwaysAgreeProtocol();
      return result ? 1 : 0;
    } catch (error) {
      return 0; // 返回默认值
    }
  }

  private onSuccess(result: LivenessSDKResultType) {
    saveLivenessActionUBT(this, '活体识别_结果成功', this.commodityId);

    ubtModuleExposeNew('finance_loan_faceID', 'finance_loan_faceID_successPopUp', this.ubtCommonParams);
    this.$emit('success', result);
  }

  private onFail(error: LivenessSDKErrorType) {
    ubtModuleExposeNew('finance_loan_faceID', 'finance_loan_faceID_failPopUp', this.ubtCommonParams);

    this.$emit('fail', error);
  }

  private onFinally() {
    this.$emit('finally');
  }

  private onCancel() {
    this.$emit('cancel');
  }

  private onInit() {
    ubtPage('finance_loan_faceID', '', new Date().getTime(), undefined, {
      scene: this.scene,
    });
  }

  private async onProtocolShow() {
    ubtModuleExposeNew('finance_loan_faceID', 'finance_loan_faceID_agreementPopUp', {
      ...this.ubtCommonParams,
      /** 1:需要每次点击同意协议 0:不需要 */
      agreeEachTime: await this.getIsNeedAlwaysAgreeProtocol(),
    });
  }

  private async onProtocolClose() {
    ubtButtonNew('finance_loan_faceID', 'finance_loan_faceID_agreementPopUp_close', {
      ...this.ubtCommonParams,
      agreeEachTime: await this.getIsNeedAlwaysAgreeProtocol(),
    });
  }

  private async onProtocolConfirm() {
    ubtButtonNew('finance_loan_faceID', 'finance_loan_faceID_agreementPopUp_agree', {
      ...this.ubtCommonParams,
      agreeEachTime: await this.getIsNeedAlwaysAgreeProtocol(),
    });
  }

  private onErrorCode201() {
    this.$ubtPageButton('臻有钱_活体识别_结果失败');
  }

  private onSdkBefore() {
    this.$ubtPageButton('臻有钱_活体识别_开始验证', 'commodityId', this.commodityId);

    ubtButtonNew('finance_loan_faceID', 'finance_loan_faceID_agree', this.ubtCommonParams);
  }

  private onNotPass() {
    saveLivenessActionUBT(this, '活体识别_结果失败', this.commodityId);
  }

  private onRetryDialogConfirm() {
    saveLivenessActionUBT(this, '活体识别失败_重新认证', this.commodityId);
    this.$ubtPageButton('臻有钱_活体识别失败_重新认证');
    ubtButtonNew('finance_loan_faceID', 'finance_loan_faceID_failPopUp_retry', this.ubtCommonParams);
  }

  private onRetryDialogClose() {
    saveLivenessActionUBT(this, '活体识别失败_取消', this.commodityId);
    this.$ubtPageButton('臻有钱_活体识别失败_取消');
  }

  private onAppBeforeLaunch() {
    saveLivenessActionUBT(this, '活体识别_调用', this.commodityId);
  }

  private onAppAfterLaunch() {
    this.$ubtPageButton('finance_upgradeAAppling_faceIDpro');
  }

  private onH5DingTalkNotSupport() {
    this.$ubtPageButton('finance_dingH5facialidentification');
  }

  private onH5NotSupport() {
    this.$ubtModuleExpose('finance_financeLoanApply_h5LivingPop');

    if (!landData.getGoDownload()) {
      Toast(`无法进行人脸验证，请联系客服${customerServicePhone}`);
    } else {
      Toast('检测到当前环境无法做活体验证，即将跳转下载哈啰app');
      setTimeout(() => {
        jumpToMarketPage();
      }, 2000);
    }
  }

  private render() {
    return (
      <LivenessView
        project={ LivenessProjectEnum.ZYQ }
        scene={ this.scene }
        systemCode={ platform.systemCode }
        standardEnv={ standardEnv }
        redirectUrl={ this.livenessRedirectUrl }
        isShowCancelBtn={ this.isShowCancelBtn }
        cancelBtnText={ this.cancelBtnText }
        requestOptions={ this.requestOptions }
        launchTransaction={ this.launchTransaction }
        skipAgreementCheck={ this.skipAgreementCheck }
        onSuccess={ this.onSuccess.bind(this) }
        onFail={ this.onFail.bind(this) }
        onFinally={ this.onFinally.bind(this) }
        onCancel={ this.onCancel.bind(this) }
        onInit={ this.onInit.bind(this) }
        onProtocolShow={ this.onProtocolShow.bind(this) }
        onProtocolClose={ this.onProtocolClose.bind(this) }
        onProtocolConfirm={ this.onProtocolConfirm.bind(this) }
        onErrorCode201={ this.onErrorCode201.bind(this) }
        onSdkBefore={ this.onSdkBefore.bind(this) }
        onNotPass={ this.onNotPass.bind(this) }
        onRetryDialogConfirm={ this.onRetryDialogConfirm.bind(this) }
        onRetryDialogClose={ this.onRetryDialogClose.bind(this) }
        onAppBeforeLaunch={ this.onAppBeforeLaunch.bind(this) }
        onAppAfterLaunch={ this.onAppAfterLaunch.bind(this) }
        onH5DingTalkNotSupport={ this.onH5DingTalkNotSupport.bind(this) }
        onH5NotSupport={ this.onH5NotSupport.bind(this) }
      />
    );
  }
}
